/**
 * Market Context Service
 *
 * Prepares market context for AI newsletter generation by combining
 * weekly/quarterly summaries with current market data.
 *
 * Part of the AI Integration Plan Phase 1: Market Context Foundation
 */

import marketDataService from './MarketDataService.js';
import HybridCache from './HybridCache.js';
import { createDataUnavailableError, isErrorResponse, logError } from '../utils/StandardErrorFormat.js';
import logger from '../utils/logger.js';

class MarketContextService {
  constructor() {
    this.logger = logger.child('MarketContextService');
    this.marketDataService = marketDataService; // Use singleton instance
    this.cache = new HybridCache({
      contextTTL: 6 * 60 * 60 * 1000  // 6 hours for combined context
    });
  }

  /**
   * Get complete market context for AI prompt injection
   * Combines weekly summary, quarterly summary, and latest market data
   * @param {Object} options - Context options
   * @returns {Promise<Object>} Market context for AI
   */
  async getMarketContext(options = {}) {
    try {
      const {
        includeWeekly = true,
        includeQuarterly = true,
        includeRealtime = true,
        newsletterType = 'weekly'  // 'weekly', 'monthly', 'quarterly'
      } = options;

      // Check cache first
      const cacheKey = `market_context_${newsletterType}_${new Date().toISOString().split('T')[0]}`;
      const cached = await this.cache.getContent('market_context', {
        type: newsletterType,
        date: new Date().toISOString().split('T')[0]
      });

      if (cached && !options.forceRefresh) {
        return cached;
      }

      const context = {
        generated_at: new Date().toISOString(),
        newsletter_type: newsletterType,
        sections: {}
      };

      // Fetch components in parallel for performance
      const promises = [];

      if (includeWeekly) {
        promises.push(
          this.marketDataService.generateWeeklySummary()
            .then(summary => { context.sections.weekly = summary; })
            .catch(err => {
              this.logger.error('Failed to get weekly summary:', err);
              // Return error object (don't throw)
              context.sections.weekly = {
                error: true,
                errorType: 'DATA_UNAVAILABLE',
                message: 'Weekly market summary unavailable',
                timestamp: new Date().toISOString()
              };
            })
        );
      }

      if (includeQuarterly && newsletterType === 'quarterly') {
        promises.push(
          this.marketDataService.generateQuarterlySummary()
            .then(summary => { context.sections.quarterly = summary; })
            .catch(err => {
              this.logger.error('Failed to get quarterly summary:', err);
              // Return error object (don't throw)
              context.sections.quarterly = {
                error: true,
                errorType: 'DATA_UNAVAILABLE',
                message: 'Quarterly market summary unavailable',
                timestamp: new Date().toISOString()
              };
            })
        );
      }

      if (includeRealtime) {
        promises.push(
          this._getRealtimeContext()
            .then(realtime => { context.sections.realtime = realtime; })
            .catch(err => {
              this.logger.error('Failed to get realtime context:', err);
              // Return error object (don't throw)
              context.sections.realtime = {
                error: true,
                errorType: 'DATA_UNAVAILABLE',
                message: 'Realtime market data unavailable',
                timestamp: new Date().toISOString()
              };
            })
        );
      }

      // Wait for all data to be fetched
      await Promise.all(promises);

      // Generate combined narrative
      context.combined_narrative = this._generateCombinedNarrative(context.sections, newsletterType);

      // Add prompt-ready formats
      context.prompt_injections = this._formatForPromptInjection(context);

      // Calculate total context size (for token management)
      const completeness = this._calculateCompleteness(context.sections);
      context.metadata = {
        total_words: this._countWords(context),
        estimated_tokens: Math.ceil(this._countWords(context) * 1.3),  // Rough token estimate
        completeness: completeness
      };

      // Add isEmpty flag for mock data transparency (CLAUDE.md Rule 15)
      // isEmpty = true when no real market data is available (completeness = 0%)
      context.isEmpty = completeness === 0;

      // Cache the result
      await this.cache.setContent('market_context', context, {
        type: newsletterType,
        date: new Date().toISOString().split('T')[0],
        ttl: 6 * 60 * 60 * 1000  // 6 hours
      });

      return context;
    } catch (error) {
      // Return error response (don't throw - tests expect returned error object)
      const errorResponse = {
        error: true,
        errorType: 'DATA_UNAVAILABLE',
        message: 'Failed to generate market context for AI',
        sections: {
          weekly: null,
          quarterly: null,
          realtime: null
        },
        isEmpty: true  // Market context is empty when error occurs
      };

      logError(error, 'MarketContextService.getMarketContext');
      return errorResponse;
    }
  }

  /**
   * Get realtime market snapshot for immediate context
   * @private
   */
  async _getRealtimeContext() {
    const data = await this.marketDataService.getLatestData('daily');

    // Check if data is available
    if (!data || data.error) {
      throw new Error('Market data unavailable');
    }

    // Extract key points for AI context (use transformed data structure)
    const indices = data.indices || {};
    const topNews = (data.news_highlights || []).slice(0, 3);

    // VIX is in indices.VIX, not market_sentiment
    const vixData = indices.VIX || { price: 15, changePercent: 0 };
    const vix = {
      value: vixData.price || 15,
      level: this._getVixLevel(vixData.price || 15)
    };

    // Fear/Greed index may not be available
    const fearGreed = data.market_sentiment?.fear_greed_index || {
      value: 50,
      level: 'Neutral'
    };

    // Generate concise realtime summary (30-40 words)
    const sp500 = indices.SPY || { price: 0, changePercent: 0 };
    const nasdaq = indices.QQQ || { price: 0, changePercent: 0 };

    const summary = `Current: S&P ${sp500.changePercent > 0 ? '+' : ''}${sp500.changePercent.toFixed(1)}% at ${sp500.price.toFixed(0)}, NASDAQ ${nasdaq.changePercent > 0 ? '+' : ''}${nasdaq.changePercent.toFixed(1)}% at ${nasdaq.price.toFixed(0)}. VIX at ${vix.value.toFixed(1)} (${vix.level}). ${topNews.length > 0 ? this._getNewsContext(topNews) : 'Markets showing steady activity.'}`;

    return {
      summary,
      timestamp: data.collection_timestamp,
      data_points: {
        indices: indices,
        vix: vix.value,
        fear_greed: fearGreed.value,
        top_headlines: topNews
      }
    };
  }

  /**
   * Generate combined narrative from all context sections
   * @private
   */
  _generateCombinedNarrative(sections, newsletterType) {
    const parts = [];

    // Start with the appropriate timeframe context
    if (newsletterType === 'quarterly' && sections.quarterly) {
      parts.push(sections.quarterly.summary);
    } else if (sections.weekly) {
      parts.push(sections.weekly.summary);
    }

    // Add realtime context if significantly different from summary
    if (sections.realtime) {
      const realtimeContext = this._shouldIncludeRealtimeUpdate(sections);
      if (realtimeContext) {
        parts.push(`Latest: ${realtimeContext}`);
      }
    }

    return parts.join(' ');
  }

  /**
   * Format context for direct injection into AI prompts
   * @private
   */
  _formatForPromptInjection(context) {
    const injections = {};

    // Weekly context placeholder
    if (context.sections.weekly) {
      injections.MARKET_CONTEXT_WEEKLY = context.sections.weekly.summary;
    }

    // Quarterly context placeholder
    if (context.sections.quarterly) {
      injections.MARKET_CONTEXT_QUARTERLY = context.sections.quarterly.summary;
    }

    // Current market conditions
    if (context.sections.realtime) {
      injections.MARKET_CONTEXT_CURRENT = context.sections.realtime.summary;
    }

    // Combined context for simple injection
    injections.MARKET_CONTEXT_COMBINED = context.combined_narrative;

    // Structured data for advanced prompts
    injections.MARKET_DATA_STRUCTURED = JSON.stringify({
      indices: context.sections.realtime?.data_points?.indices || {},
      sentiment: {
        vix: context.sections.realtime?.data_points?.vix,
        fear_greed: context.sections.realtime?.data_points?.fear_greed
      },
      themes: context.sections.quarterly?.metadata?.key_themes || []
    });

    return injections;
  }

  /**
   * Helper: Get VIX level description
   * @private
   */
  _getVixLevel(vixValue) {
    if (vixValue < 12) return 'Low volatility';
    if (vixValue < 20) return 'Normal';
    if (vixValue < 30) return 'Elevated';
    return 'High volatility';
  }

  /**
   * Helper: Generate news context from headlines
   * @private
   */
  _getNewsContext(headlines) {
    if (!headlines || headlines.length === 0) return '';

    // Count sentiment
    const sentiments = headlines.map(h => h.sentiment);
    const positive = sentiments.filter(s => s === 'positive').length;
    const negative = sentiments.filter(s => s === 'negative').length;

    if (positive > negative) {
      return 'Headlines lean positive.';
    } else if (negative > positive) {
      return 'Headlines show caution.';
    } else {
      return 'Mixed headline sentiment.';
    }
  }

  /**
   * Helper: Determine if realtime update adds value
   * @private
   */
  _shouldIncludeRealtimeUpdate(sections) {
    if (!sections.realtime || !sections.weekly) return null;
    if (!sections.weekly.metadata || !sections.realtime.data_points) return null;

    // Check if market moved significantly since weekly summary
    const currentVix = sections.realtime.data_points.vix;
    const weeklyVix = sections.weekly.metadata.vix_level;

    if (!currentVix || !weeklyVix) return null;

    if (Math.abs(currentVix - weeklyVix) > 3) {
      return `VIX ${currentVix > weeklyVix ? 'spiked' : 'dropped'} to ${currentVix.toFixed(1)}`;
    }

    return null;
  }

  /**
   * Helper: Count total words in context
   * @private
   */
  _countWords(context) {
    let totalWords = 0;

    if (context.sections.weekly?.summary) {
      totalWords += context.sections.weekly.summary.split(' ').length;
    }
    if (context.sections.quarterly?.summary) {
      totalWords += context.sections.quarterly.summary.split(' ').length;
    }
    if (context.sections.realtime?.summary) {
      totalWords += context.sections.realtime.summary.split(' ').length;
    }

    return totalWords;
  }

  /**
   * Helper: Calculate context completeness percentage
   * @private
   */
  _calculateCompleteness(sections) {
    // Count sections that are not null and not error responses (MARKET-DATA-004 Phase 2)
    const available = Object.values(sections).filter(s => s !== null && !isErrorResponse(s)).length;
    const total = Object.keys(sections).length;
    return Math.round((available / total) * 100);
  }

  /**
   * Get market context specifically formatted for newsletter type
   * @param {string} type - Newsletter type ('client', 'market_update', 'quarterly_review')
   * @returns {Promise<Object>} Formatted context
   */
  async getContextForNewsletterType(type) {
    const contextConfig = {
      client: {
        includeWeekly: true,
        includeQuarterly: false,
        includeRealtime: true,
        newsletterType: 'weekly'
      },
      market_update: {
        includeWeekly: true,
        includeQuarterly: false,
        includeRealtime: true,
        newsletterType: 'weekly'
      },
      quarterly_review: {
        includeWeekly: false,
        includeQuarterly: true,
        includeRealtime: true,
        newsletterType: 'quarterly'
      }
    };

    const config = contextConfig[type] || contextConfig.market_update;
    return await this.getMarketContext(config);
  }

  /**
   * Validate market context meets requirements
   * @param {Object} context - Market context object
   * @returns {Object} Validation result
   */
  validateContext(context) {
    const issues = [];
    const warnings = [];

    // Check word counts
    if (context.sections.weekly) {
      const wordCount = context.sections.weekly.summary.split(' ').length;
      if (wordCount < 40) {
        issues.push('Weekly summary too short (< 40 words)');
      } else if (wordCount > 60) {
        warnings.push('Weekly summary exceeds target (> 60 words)');
      }
    }

    if (context.sections.quarterly) {
      const wordCount = context.sections.quarterly.summary.split(' ').length;
      if (wordCount < 60) {
        issues.push('Quarterly summary too short (< 60 words)');
      } else if (wordCount > 80) {
        warnings.push('Quarterly summary exceeds target (> 80 words)');
      }
    }

    // Check data freshness
    if (context.sections.realtime) {
      const dataAge = Date.now() - new Date(context.sections.realtime.timestamp).getTime();
      const hoursOld = dataAge / (1000 * 60 * 60);

      if (hoursOld > 24) {
        issues.push('Realtime data is stale (> 24 hours old)');
      } else if (hoursOld > 6) {
        warnings.push('Realtime data may be outdated (> 6 hours old)');
      }
    }

    // Check completeness
    if (context.metadata.completeness < 75) {
      warnings.push(`Context only ${context.metadata.completeness}% complete`);
    }

    return {
      valid: issues.length === 0,
      issues,
      warnings,
      metadata: {
        total_words: context.metadata.total_words,
        estimated_tokens: context.metadata.estimated_tokens,
        completeness: context.metadata.completeness
      }
    };
  }
}

// Create singleton instance
const marketContextService = new MarketContextService();

export { MarketContextService };
export default marketContextService;